import {
  articleDetailSchema,
  articleGroupListSchema,
  articleListResponseSchema,
  articlePriceListSchema,
  articleSupplierOptionListSchema,
  type ArticleListQuery,
  type CreateArticleGroupRequest,
  type CreateArticlePurchasePriceRequest,
  type CreateArticleRequest,
  type CreateArticleSupplierRequest,
  type CreateArticleUnitFactorRequest,
  type PatchArticleGroupRequest,
  type PatchArticleRequest,
  type PatchArticleSupplierRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { API_BASE_URL, ApiRequestError, apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const articlesQueryKey = ['articles'] as const;

const createdSchema = z.object({ id: z.string().uuid() }).passthrough();

function listParams(query: Partial<ArticleListQuery> & { includeInactive?: boolean }) {
  return toSearchParams({
    page: query.page,
    pageSize: query.pageSize,
    q: query.q,
    includeInactive: query.includeInactive === true ? 'true' : undefined,
    type: query.type,
    groupId: query.groupId,
    stockManaged:
      query.stockManaged === undefined ? undefined : query.stockManaged ? 'true' : 'false',
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  });
}

export function useArticles(
  query: Partial<ArticleListQuery> & { includeInactive?: boolean } = {
    page: 1,
    pageSize: 25,
    includeInactive: true,
    sortBy: 'articleNumber',
    sortOrder: 'asc',
  },
) {
  return useQuery({
    queryKey: [...articlesQueryKey, 'list', query],
    queryFn: () => apiFetch(`/articles${listParams(query)}`, articleListResponseSchema),
  });
}

export function useArticle(id: string) {
  return useQuery({
    queryKey: [...articlesQueryKey, 'detail', id],
    enabled: id.length > 0,
    queryFn: () => apiFetch(`/articles/${id}`, articleDetailSchema),
  });
}

export function useArticleGroups(includeInactive = true) {
  return useQuery({
    queryKey: [...articlesQueryKey, 'groups', { includeInactive }],
    queryFn: () =>
      apiFetch(
        `/articles/groups${toSearchParams({ includeInactive: includeInactive ? 'true' : 'false' })}`,
        articleGroupListSchema,
      ),
  });
}

export function useArticleSupplierOptions(enabled: boolean) {
  return useQuery({
    queryKey: [...articlesQueryKey, 'supplier-partners'],
    enabled,
    queryFn: () => apiFetch('/articles/supplier-partners', articleSupplierOptionListSchema),
  });
}

export function useArticlePrices(articleId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...articlesQueryKey, 'prices', articleId],
    enabled: enabled && articleId.length > 0,
    queryFn: () => apiFetch(`/articles/${articleId}/prices`, articlePriceListSchema),
  });
}

export function useCreateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateArticleRequest) =>
      apiFetch('/articles', articleDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function usePatchArticle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchArticleRequest) =>
      apiFetch(`/articles/${id}`, articleDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function useCreateArticleGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateArticleGroupRequest) =>
      apiFetch('/articles/groups', createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function usePatchArticleGroup(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchArticleGroupRequest) =>
      apiFetch(`/articles/groups/${id}`, createdSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function useAddArticleSupplier(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateArticleSupplierRequest) =>
      apiFetch(`/articles/${articleId}/suppliers`, createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function usePatchArticleSupplier(articleId: string, supplierId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchArticleSupplierRequest) =>
      apiFetch(`/articles/${articleId}/suppliers/${supplierId}`, createdSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function useAddArticlePrice(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateArticlePurchasePriceRequest) =>
      apiFetch(`/articles/${articleId}/prices`, createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function useAddArticleUnitFactor(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateArticleUnitFactorRequest) =>
      apiFetch(`/articles/${articleId}/unit-factors`, createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export function useRemoveArticleDocument(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiFetch(`/articles/${articleId}/documents/${documentId}`, createdSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}

export async function downloadArticleDocument(
  articleId: string,
  documentId: string,
  fileName: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/articles/${articleId}/documents/${documentId}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiRequestError({
      statusCode: response.status,
      code: 'UNKNOWN',
      message: payload?.message ?? 'Das Dokument konnte nicht geöffnet werden.',
      timestamp: new Date().toISOString(),
      path: `/articles/${articleId}/documents/${documentId}`,
    });
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function useUploadArticleDocument(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, file }: { kind: string; file: File }) => {
      const body = new FormData();
      body.append('kind', kind);
      body.append('file', file);
      return apiFetch(`/articles/${articleId}/documents`, createdSchema, {
        method: 'POST',
        body,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articlesQueryKey });
    },
  });
}
